import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, MessageSquare, CheckCircle, RefreshCw, Clock, Send, User, Bot } from 'lucide-react';

interface TaskEvent {
  id: string;
  timestamp: Date;
  type: 'broadcast' | 'claim' | 'delete' | 'dm_sent' | 'complete' | 'release' | 'rebroadcast';
  data: any;
  actor?: string;
}

interface Task {
  id: string;
  description: string;
  reward: string;
  status: 'pending' | 'claimed' | 'completed' | 'released';
  claimedBy?: string;
  messageTs?: string;
  events: TaskEvent[];
}

export default function TaskTapFlow() {
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [latestEvents, setLatestEvents] = useState<TaskEvent[]>([]);
  const [testMode, setTestMode] = useState(false);
  const [ws, setWs] = useState<WebSocket | null>(null);

  // Connect to WebSocket for real-time updates
  useEffect(() => {
    // For now, we'll poll the event endpoint
    const pollEvents = async () => {
      try {
        const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/tasktap/events');
        if (response.ok) {
          const events = await response.json();
          processEvents(events);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
      }
    };

    const interval = setInterval(pollEvents, 2000);
    return () => clearInterval(interval);
  }, []);

  const processEvents = (events: TaskEvent[]) => {
    setLatestEvents(events.slice(-10));

    const taskMap = { ...tasks };
    events.forEach(event => {
      const taskId = event.data.taskId || event.id;
      if (!taskMap[taskId]) {
        taskMap[taskId] = {
          id: taskId,
          description: event.data.task || 'Unknown task',
          reward: event.data.reward || '$5',
          status: 'pending',
          events: []
        };
      }

      taskMap[taskId].events.push(event);

      // Update task status based on event
      switch (event.type) {
        case 'claim':
          taskMap[taskId].status = 'claimed';
          taskMap[taskId].claimedBy = event.data.userId;
          break;
        case 'complete':
          taskMap[taskId].status = 'completed';
          break;
        case 'release':
          taskMap[taskId].status = 'pending';
          taskMap[taskId].claimedBy = undefined;
          break;
      }
    });

    setTasks(taskMap);
  };

  const sendTestTask = async () => {
    const task = {
      task: `Test Task ${Date.now()}`,
      reward: '$' + Math.floor(Math.random() * 20 + 5),
      channel: 'C09H9EC5W7N'
    };

    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/tasktap/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(task)
      });

      const result = await response.json();

      // Add to local state immediately
      const event: TaskEvent = {
        id: result.taskId,
        timestamp: new Date(),
        type: 'broadcast',
        data: { ...task, taskId: result.taskId, messageTs: result.message_ts }
      };

      processEvents([event]);
    } catch (error) {
      console.error('Failed to send task:', error);
    }
  };

  const getEventIcon = (type: TaskEvent['type']) => {
    switch (type) {
      case 'broadcast': return <Send className="w-4 h-4" />;
      case 'claim': return <User className="w-4 h-4" />;
      case 'delete': return <MessageSquare className="w-4 h-4" />;
      case 'dm_sent': return <Bot className="w-4 h-4" />;
      case 'complete': return <CheckCircle className="w-4 h-4" />;
      case 'release': return <RefreshCw className="w-4 h-4" />;
      case 'rebroadcast': return <Send className="w-4 h-4" />;
    }
  };

  const getEventColor = (type: TaskEvent['type']) => {
    switch (type) {
      case 'broadcast': return 'bg-blue-500';
      case 'claim': return 'bg-green-500';
      case 'delete': return 'bg-red-500';
      case 'dm_sent': return 'bg-purple-500';
      case 'complete': return 'bg-green-600';
      case 'release': return 'bg-yellow-500';
      case 'rebroadcast': return 'bg-blue-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">TaskTap Flow Visualizer</h1>
          <p className="text-gray-400">Real-time visualization of task distribution through Slack</p>
        </div>

        {/* Control Panel */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <button
              onClick={sendTestTask}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
            >
              Broadcast Test Task
            </button>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm">Connected</span>
              </div>
              <div className="text-sm text-gray-400">
                Active Tasks: {Object.keys(tasks).length}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Flow Diagram */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Task Lifecycle Flow</h2>

            <div className="space-y-6">
              {/* Broadcast */}
              <div className="flex items-center gap-4">
                <div className="w-32 text-right">API</div>
                <ArrowRight className="w-5 h-5 text-gray-500" />
                <div className="flex-1 bg-blue-500/20 border border-blue-500 rounded-lg p-3">
                  <div className="font-medium">Broadcast Task</div>
                  <div className="text-sm text-gray-400">POST /tasktap/broadcast</div>
                </div>
              </div>

              {/* To Slack */}
              <div className="flex items-center gap-4">
                <div className="w-32 text-right">Worker</div>
                <ArrowRight className="w-5 h-5 text-gray-500" />
                <div className="flex-1 bg-purple-500/20 border border-purple-500 rounded-lg p-3">
                  <div className="font-medium">Send to Slack Channel</div>
                  <div className="text-sm text-gray-400">chat.postMessage with button</div>
                </div>
              </div>

              {/* User Claims */}
              <div className="flex items-center gap-4">
                <div className="w-32 text-right">User</div>
                <ArrowRight className="w-5 h-5 text-gray-500" />
                <div className="flex-1 bg-green-500/20 border border-green-500 rounded-lg p-3">
                  <div className="font-medium">Claims Task</div>
                  <div className="text-sm text-gray-400">Clicks "Claim Task" button</div>
                </div>
              </div>

              {/* Interactive Callback */}
              <div className="flex items-center gap-4">
                <div className="w-32 text-right">Slack</div>
                <ArrowRight className="w-5 h-5 text-gray-500" />
                <div className="flex-1 bg-yellow-500/20 border border-yellow-500 rounded-lg p-3">
                  <div className="font-medium">Interactive Callback</div>
                  <div className="text-sm text-gray-400">POST /slack/interactive</div>
                </div>
              </div>

              {/* Delete & DM */}
              <div className="flex items-center gap-4">
                <div className="w-32 text-right">Worker</div>
                <ArrowRight className="w-5 h-5 text-gray-500" />
                <div className="flex-1 space-y-2">
                  <div className="bg-red-500/20 border border-red-500 rounded-lg p-3">
                    <div className="font-medium">Delete Message</div>
                    <div className="text-sm text-gray-400">chat.delete</div>
                  </div>
                  <div className="bg-purple-500/20 border border-purple-500 rounded-lg p-3">
                    <div className="font-medium">Send DM</div>
                    <div className="text-sm text-gray-400">DM with Complete/Release buttons</div>
                  </div>
                </div>
              </div>

              {/* Complete/Release */}
              <div className="flex items-center gap-4">
                <div className="w-32 text-right">User DM</div>
                <ArrowRight className="w-5 h-5 text-gray-500" />
                <div className="flex-1 space-y-2">
                  <div className="bg-green-600/20 border border-green-600 rounded-lg p-3">
                    <div className="font-medium">Mark Complete</div>
                    <div className="text-sm text-gray-400">Updates DB, confirms completion</div>
                  </div>
                  <div className="bg-yellow-600/20 border border-yellow-600 rounded-lg p-3">
                    <div className="font-medium">Release Task</div>
                    <div className="text-sm text-gray-400">Rebroadcasts to channel</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Live Events */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Live Event Stream</h2>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {latestEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No events yet. Send a test task to see the flow!
                </div>
              ) : (
                latestEvents.map((event, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-700/50 rounded-lg">
                    <div className={`p-2 rounded-full ${getEventColor(event.type)}`}>
                      {getEventIcon(event.type)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium capitalize">
                        {event.type.replace('_', ' ')}
                      </div>
                      <div className="text-sm text-gray-400">
                        {event.data.task || event.data.taskId}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Active Tasks */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Active Tasks</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(tasks).map(task => (
              <div key={task.id} className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-medium text-sm truncate">
                    {task.description}
                  </div>
                  <div className="text-green-400 font-mono text-sm">
                    {task.reward}
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    task.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    task.status === 'claimed' ? 'bg-blue-500/20 text-blue-400' :
                    task.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {task.status}
                  </div>
                  {task.claimedBy && (
                    <div className="text-xs text-gray-400">
                      by {task.claimedBy}
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  {task.events.slice(-3).map((event, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>{event.type}</span>
                      <span className="text-gray-600">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}